import { createContext, useContext, useEffect, useRef } from "react";
import { useMedia } from "@remi/next-ui/hooks";

import type { ConversationChannel, ParticipantRole } from "~/components/escalation/hub/hub-types";

export const MAX_POPOUTS = 3;

export type PopoutChannel = ConversationChannel;

export interface PopoutParticipant {
  name: string;
  role: ParticipantRole;
}

export interface PopoutConversation {
  id: string;
  channel: PopoutChannel;
  rawChannel?: string;
  channelName: string;
  bidId: number | null;
  participants: PopoutParticipant[];
}

export interface PopoutTicket {
  id: string;
  titleHint: string;
  homeownerNameHint: string | null;
  workSiteId?: number;
}

export interface PopoutTask {
  id: string;
  titleHint: string;
  homeownerNameHint: string | null;
}

export type PopoutContent =
  | { kind: "conversation"; conversation: PopoutConversation }
  | { kind: "ticket"; ticket: PopoutTicket }
  | { kind: "task"; task: PopoutTask };

type PopoutStatus = "open" | "minimized";

export interface PopoutSlot {
  popoutId: string;
  content: PopoutContent;
  status: PopoutStatus;
  initialOffset: { x: number; y: number };
  zIndex: number;
  lastInteractedAt: number;
}

export type PopoutSlotOfKind<K extends PopoutContent["kind"]> = Omit<PopoutSlot, "content"> & {
  content: Extract<PopoutContent, { kind: K }>;
};

export interface PopoutState {
  popouts: PopoutSlot[];
}

export const getContentKey = (content: PopoutContent): string => {
  switch (content.kind) {
    case "ticket":
      return `ticket:${content.ticket.id}`;
    case "task":
      return `task:${content.task.id}`;
    case "conversation":
      return `conversation:${content.conversation.id}`;
  }
};

export const getPopoutAriaLabel = (content: PopoutContent): string => {
  switch (content.kind) {
    case "ticket": {
      const { titleHint, homeownerNameHint } = content.ticket;
      return homeownerNameHint ? `Ticket: ${titleHint} — ${homeownerNameHint}` : `Ticket: ${titleHint}`;
    }
    case "task": {
      const { titleHint, homeownerNameHint } = content.task;
      return homeownerNameHint ? `Task: ${titleHint} — ${homeownerNameHint}` : `Task: ${titleHint}`;
    }
    case "conversation": {
      const { conversation } = content;
      if (conversation.channel === "internal") {
        return conversation.channelName;
      }
      return conversation.participants.map((p) => p.name).join(" and ");
    }
  }
};

interface PopoutManagerContextValue {
  state: PopoutState;
  openConversationPopout: (conversation: PopoutConversation) => void;
  openTicketPopout: (ticket: PopoutTicket) => void;
  openTaskPopout: (task: PopoutTask) => void;
  openPopouts: (contents: PopoutContent[]) => void;
  closePopout: (popoutId: string) => void;
  minimizePopout: (popoutId: string) => void;
  minimizeAllPopouts: () => void;
  restorePopout: (popoutId: string) => void;
  raisePopout: (popoutId: string) => void;
}

const NOOP_VALUE: PopoutManagerContextValue = {
  state: { popouts: [] },
  openConversationPopout: () => {},
  openTicketPopout: () => {},
  openTaskPopout: () => {},
  openPopouts: () => {},
  closePopout: () => {},
  minimizePopout: () => {},
  minimizeAllPopouts: () => {},
  restorePopout: () => {},
  raisePopout: () => {},
};

export const PopoutManagerContext = createContext<PopoutManagerContextValue>(NOOP_VALUE);

export const usePopoutManager = () => useContext(PopoutManagerContext);

export const useConversationPopout = () => {
  const { state, openConversationPopout, closePopout, minimizePopout, restorePopout, raisePopout } = usePopoutManager();
  return { state, openConversationPopout, closePopout, minimizePopout, restorePopout, raisePopout };
};

export const useTicketPopout = () => {
  const { state, openTicketPopout, closePopout, minimizePopout, restorePopout, raisePopout } = usePopoutManager();
  return { state, openTicketPopout, closePopout, minimizePopout, restorePopout, raisePopout };
};

export const useTaskPopout = () => {
  const { state, openTaskPopout, closePopout, minimizePopout, restorePopout, raisePopout } = usePopoutManager();
  return { state, openTaskPopout, closePopout, minimizePopout, restorePopout, raisePopout };
};

// Drawers that would visually overlap popouts should call this — when the drawer
// opens, the popouts that were open dock to the corner pill (still mounted, so draft
// text survives), and when the drawer closes those same popouts restore themselves.
// Only the pill's close button actually closes a popout.
export const useMinimizePopoutsOnDrawerOpen = (open: boolean): void => {
  const { state, minimizeAllPopouts, restorePopout } = usePopoutManager();
  const { isMobile } = useMedia();
  const popoutsRef = useRef(state.popouts);
  useEffect(() => {
    popoutsRef.current = state.popouts;
  }, [state.popouts]);
  const minimizedByDrawerRef = useRef<string[]>([]);
  useEffect(() => {
    // On mobile a popout renders as a full-screen sheet, so an overlapping drawer just stacks over
    // it — docking to a pill would strand any drawer opened from inside the popout's own body.
    if (isMobile) {
      return;
    }
    if (open) {
      minimizedByDrawerRef.current = popoutsRef.current.filter((p) => p.status === "open").map((p) => p.popoutId);
      minimizeAllPopouts();
      return;
    }
    for (const popoutId of minimizedByDrawerRef.current) {
      restorePopout(popoutId);
    }
    minimizedByDrawerRef.current = [];
  }, [open, isMobile, minimizeAllPopouts, restorePopout]);
};
